/**
 * WebGPU 管线管理器。
 *
 * 职责：
 * 1. 基于 Viewer 当前相机构建渲染管线；
 * 2. 统一接管主场景与辅助场景的合成输出；
 * 3. 在相机切换或尺寸变化时重建管线；
 * 4. 管理 PassNode / RenderPipeline 生命周期，避免渲染资源散落在 Viewer 中。
 */

import * as THREE from 'three/webgpu';
import { pass, vec4 } from 'three/tsl';
import { SectionViewScenePassNode } from '../../section-view';
import type { SectionViewCapRenderStageSource } from '../../section-view';
import type { Viewer } from '../Viewer';

/**
 * 管线生命周期状态。
 */
enum PipelineLifecycleState {
    /** 尚未完成首次管线构建。 */
    NotInitialized = 'not-initialized',
    /** 管线已可用，可执行渲染。 */
    Ready = 'ready',
    /** 管线已销毁，不允许再次渲染。 */
    Disposed = 'disposed',
}

/**
 * WebGPU 渲染管线管理器。
 */
export class PipelineManager {
    /** Viewer 实例。 */
    private readonly viewer: Viewer;
    /** WebGPU 渲染器实例。 */
    private readonly renderer: THREE.WebGPURenderer;
    /** 主场景 Pass 使用的稳定剖面盖子阶段来源。 */
    private readonly sectionViewCapRenderStageSource: SectionViewCapRenderStageSource;
    /** 当前后处理管线实例。 */
    private renderPipeline: THREE.RenderPipeline | null = null;
    /** 主场景离屏渲染 Pass。 */
    private scenePass: THREE.PassNode | null = null;
    /** 辅助场景离屏渲染 Pass。 */
    private helperPass: THREE.PassNode | null = null;
    /** 管线构建时绑定的相机引用。 */
    private boundCamera: THREE.Camera | null = null;
    /** 当前生命周期状态。 */
    private lifecycleState: PipelineLifecycleState = PipelineLifecycleState.NotInitialized;

    /**
     * 构造函数。
     *
     * @param viewer Viewer 实例。
     * @param renderer WebGPU 渲染器实例。
     * @param sectionViewCapRenderStageSource 稳定剖面盖子阶段来源。
     */
    constructor(
        viewer: Viewer,
        renderer: THREE.WebGPURenderer,
        sectionViewCapRenderStageSource: SectionViewCapRenderStageSource,
    ) {
        this.viewer = viewer;
        this.renderer = renderer;
        this.sectionViewCapRenderStageSource = sectionViewCapRenderStageSource;
        this.rebuildPipeline();
    }

    /**
     * 执行一次渲染。
     *
     * @throws 当前没有可用相机或管线未初始化时抛出明确错误。
     */
    public render(): void {
        this.assertRenderable();
        const activeCamera = this.viewer.camera;
        if (!activeCamera) {
            throw new Error('当前没有可用相机，无法执行 WebGPU 管线渲染');
        }

        if (this.boundCamera !== activeCamera) {
            this.rebuildPipeline();
        }
        if (!this.renderPipeline) {
            throw new Error('WebGPU 管线未初始化完成，无法执行渲染');
        }

        const currentToneMapping = this.renderer.toneMapping;
        const currentOutputColorSpace = this.renderer.outputColorSpace;
        const currentXrEnabled = this.renderer.xr.enabled;
        try {
            this.renderPipeline.render();
        } finally {
            /**
             * Three.js RenderPipeline 会在最终 Quad 提交前临时改写这三项，但其实现没有异常恢复边界。
             * 剖面子阶段或最终 Quad 抛错时必须由 PuppyCAD 管线门面恢复，避免污染后续全部 Viewer 帧。
             */
            this.renderer.toneMapping = currentToneMapping;
            this.renderer.outputColorSpace = currentOutputColorSpace;
            this.renderer.xr.enabled = currentXrEnabled;
        }
    }

    /**
     * 响应尺寸变化，重建渲染管线。
     */
    public resize(): void {
        this.assertRenderable();
        this.rebuildPipeline();
    }

    /**
     * 销毁管线资源。
     */
    public dispose(): void {
        if (this.lifecycleState === PipelineLifecycleState.Disposed) {
            return;
        }

        this.releasePipelineResources();
        this.lifecycleState = PipelineLifecycleState.Disposed;
    }

    /**
     * 重建管线。
     *
     * @throws 管线已销毁或没有可用相机时抛出明确错误。
     */
    public rebuildPipeline(): void {
        if (this.lifecycleState === PipelineLifecycleState.Disposed) {
            throw new Error('WebGPU 管线已销毁，禁止重建');
        }

        const activeCamera = this.viewer.camera;
        if (!activeCamera) {
            throw new Error('当前没有可用相机，无法构建 WebGPU 管线');
        }

        this.releasePipelineResources();

        /**
         * 剖面盖通过主场景 Pass 的 stencil 递增/递减结果确定截面区域。
         * PassNode 默认 RenderTarget 不分配 stencil，因此必须在创建时显式开启；
         * 辅助场景不参与剖面盖，继续使用无 stencil 的轻量 RenderTarget。
        */
        const scenePass = new SectionViewScenePassNode(
            this.viewer.scene,
            activeCamera,
            this.sectionViewCapRenderStageSource,
        );
        const helperPass = pass(this.viewer.sceneHelpers, activeCamera);
        const sceneDepthTexture = scenePass.renderTarget.depthTexture;
        if (!sceneDepthTexture) {
            scenePass.dispose();
            helperPass.dispose();
            throw new Error('剖面视图主场景 Pass 缺少深度模板纹理，无法构建渲染管线');
        }
        /**
         * PassNode 即使收到 stencilBuffer=true，仍会创建默认 DepthFormat 的显式 DepthTexture。
         * WebGPU 后端检测到这份显式纹理后不会再替换为 DepthStencilFormat，最终会把模板操作
         * 绑定到没有 stencil aspect 的附件。必须在首次提交前把同一纹理升级为深度模板格式。
         */
        sceneDepthTexture.format = THREE.DepthStencilFormat;
        sceneDepthTexture.type = THREE.UnsignedInt248Type;
        sceneDepthTexture.needsUpdate = true;
        this.configureScenePassUpdateMode(scenePass);
        this.configureScenePassUpdateMode(helperPass);

        const sceneColor = scenePass.getTextureNode('output');
        const helperColor = helperPass.getTextureNode('output');
        const mergedOutput = vec4(
            sceneColor.rgb.add(helperColor.rgb),
            sceneColor.a.add(helperColor.a),
        );

        this.scenePass = scenePass;
        this.helperPass = helperPass;
        this.renderPipeline = new THREE.RenderPipeline(this.renderer, mergedOutput);
        this.boundCamera = activeCamera;
        this.lifecycleState = PipelineLifecycleState.Ready;
    }

    /**
     * 配置场景 Pass 的刷新粒度。
     *
     * @param scenePass 待配置的 three.js PassNode。
     */
    private configureScenePassUpdateMode(scenePass: THREE.PassNode): void {
        /**
         * three.js PassNode 默认按 frameId 刷新。PuppyCAD 的交互命令允许在同一浏览器帧内多次
         * 调用 requestImmediateRender；若 Pass 仍按 frame 更新，组件 transform 已写入 CPU 场景后，
         * WebGPU 主场景纹理可能继续复用本帧早些时候的旧内容，导致实体 mesh 视觉位置滞后。
         * 改为按 render 更新后，每次管线提交都会重新采样主场景和辅助场景，但不重建材质、几何体或管线。
         */
        scenePass.updateBeforeType = THREE.NodeUpdateType.RENDER;
    }

    /**
     * 释放当前管线持有的 WebGPU / RenderTarget 资源。
     */
    private releasePipelineResources(): void {
        this.renderPipeline?.dispose();
        this.scenePass?.dispose();
        this.helperPass?.dispose();
        this.renderPipeline = null;
        this.scenePass = null;
        this.helperPass = null;
        this.boundCamera = null;
    }

    /**
     * 断言当前管线可以渲染。
     */
    private assertRenderable(): void {
        if (this.lifecycleState === PipelineLifecycleState.Disposed) {
            throw new Error('WebGPU 管线已销毁，无法执行渲染');
        }
    }
}
